import axios from 'axios'
import aqe from '@pushly/aqe'
import type { DomainService } from '../../../services'
import { simpleNotification } from '../../../_utils/utils'

/** One page of tag names, and where the page after it is. */
export interface PageTagPage {
    names: string[]
    /** The next page's address, or `null` when this was the last. */
    next: string | null
}

export interface PageTagReadRequest {
    /** Narrows the page to tags matching this. Absent reads the list unnarrowed. */
    search?: string
    /** A previous page's `next`. Absent reads the first page. */
    cursor?: string
    /** Abandons the read. A read that sees this raised must not resolve with results. */
    signal: AbortSignal
}

/**
 * Where page tags are read from and recorded.
 *
 * An interface rather than a direct call so the control the reader sees is a
 * function of what it is given: the levels disagree about whether a new tag can be
 * recorded at all, and a caller can state either answer without a server.
 */
export interface PageTagSource {
    read(request: PageTagReadRequest): Promise<PageTagPage>
    /**
     * Records a tag the reader named and resolves to the name to store, or `null`
     * when it could not be recorded and nothing should be stored.
     */
    create(name: string): Promise<string | null>
}

/** The largest page the listing endpoints will serve. */
const PAGE_LIMIT = 250

export type PageTagScope = { level: 'domain'; domainId: number } | { level: 'org'; orgId: number }

/** The part of the domain service this needs, named so a caller can stand it in. */
export type PageTagWriter = Pick<DomainService, 'addPageTagsToDomain'>

/**
 * Where the level's page tags are listed.
 *
 * Resolved at the moment a read happens rather than when the source is built: the
 * platform host comes from application configuration, and reading it early would
 * make merely having a source depend on that configuration being in place.
 */
function listUrl(scope: PageTagScope): string {
    return scope.level === 'domain'
        ? `${aqe.defaults.publicApiDomain}/v3/domains/${scope.domainId}/page-tags`
        : `${aqe.defaults.publicApiDomain}/v3/accounts/${scope.orgId}/page-tags`
}

/**
 * Reads a level's page tags, and records new ones where the level keeps them.
 *
 * Only a domain owns page tags. Above one, a name the reader types is stored on the
 * segment and nowhere else — there is no shared list for it to join, so recording it
 * would invent one.
 */
export function createPageTagSource(scope: PageTagScope, writer?: PageTagWriter): PageTagSource {
    return {
        async read({ search, cursor, signal }) {
            // The transport predates abort signals, so the signal is adapted onto the
            // cancellation it does understand. Both directions matter: a signal already
            // raised must not start a request that nothing will stop.
            const cancellation = axios.CancelToken.source()
            const stop = () => cancellation.cancel()

            if (signal.aborted) stop()
            else signal.addEventListener('abort', stop, { once: true })

            try {
                const { data } = await axios.get(cursor ?? listUrl(scope), {
                    cancelToken: cancellation.token,
                    // A page address already carries where it is in the list; restating
                    // the first page's parameters over it would read page one forever.
                    params: cursor
                        ? undefined
                        : { page: 1, limit: PAGE_LIMIT, ...(search === undefined ? {} : { search }) },
                })

                const names = Array.isArray(data?.data)
                    ? data.data
                          .map((tag: { name?: string }) => tag?.name)
                          .filter((name: unknown): name is string => typeof name === 'string' && name.length > 0)
                    : []

                return { names, next: data?.links?.next ?? null }
            } finally {
                signal.removeEventListener('abort', stop)
            }
        },

        async create(name) {
            if (scope.level !== 'domain' || !writer) return name

            const response = await writer.addPageTagsToDomain(scope.domainId, [name])
            const saved = response.ok ? response.data?.[0]?.name : undefined

            if (!saved) return null

            simpleNotification('success', `${saved} Page Tag was successfully saved.`)

            return saved
        },
    }
}
